import { c as _c } from "react/compiler-runtime";
import { CollisionDetection, DndContext, DragEndEvent, DragOverlay, pointerWithin, rectIntersection, useDndContext, useDroppable } from '@dnd-kit/core';
import { AnimateLayoutChanges, defaultAnimateLayoutChanges, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, ButtonBase, Collapse, Fade, Stack, Typography } from '@mui/material';
import { forwardRef, memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { DotsMoveIcon } from '@/components/icons/DotsMoveIcon';
import { MinusCircleIcon } from '@/components/icons/MinusCircleIcon';
import { PlusCircleIcon } from '@/components/icons/PlusCircleIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { RefreshCcw01Icon } from '@/components/icons/RefreshCcw01Icon';
import { RefreshCw01Icon } from '@/components/icons/RefreshCw01Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { ResizeLine } from '@/components/layout/SplitScreenLayout/ResizeLine';
import { measuringConfig, SortableList, useDndKitSensors } from '@/components/lists/DragAndDropList/SortableList';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import type { GraphQLValidationIssue } from '@/graphql/errors';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useGetState } from '@/hooks/useGetState';
import { FormValidationIssues } from '@/modules/forms/FormValidationIssues';
import { createRecursiveFormNode, RECURSIVE_FORM_LIMITS } from '@/modules/forms/recursiveFormConfig';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { readSalesforceMapping, removeSalesforceDestination, setSalesforceAnswerDestination, setSalesforceEvidenceDestination } from '@/modules/salesforce/formMapping';
import { SalesforceDestinationBanner } from '@/modules/salesforce/SalesforceDestinationBanner';
import { SalesforceQuestionMappingEditor } from '@/modules/salesforce/SalesforceQuestionMappingEditor';
import { COLORS } from '@/styles/tokens/colors';
import { InlineTitleEditor } from './AdminFormBuilderInlineEditors';
import { ACTIVATION_OPERATOR_LABELS, FIELD_TYPE_OPTIONS, QuestionInspector } from './AdminFormBuilderQuestionInspector';
import { childFields, ComposerField as FormField, composerFields as getFormFields, composerPositions as getQuestionPositions, ComposerRow as NestedQuestionRow, composerRows as getNestedQuestionRows, composerSchemaDepth, ComposerSection as FormSection, mapComposerFields as mapFields, moveComposerField, persistComposerDraft, removeComposerField, structuralFields } from './AdminFormComposer.model';
import { useNormalizeFormDraftConfigMutation } from './AdminFormsPage.generated';
import { addSingleSelectFollowUp, createBlankField, createBlankSection, FormDraft, FormDraftInput, getFieldOptionsError, getMaxLengthError, getOnlyActiveWhenOperators, hydrateFormDraft, removeFormSection, reorderSections, setFormFieldActivationParent, updateFormField } from './AdminFormsPage.model';
import { FormExtractionPromptModal } from './FormExtractionPromptModal';
import { FormJsonImportModal } from './FormJsonImportModal';
import { useFormBuilderPanelWidths } from './hooks/useFormBuilderPanelWidths';
import { useFormDraftHistory } from './hooks/useFormDraftHistory';
import { mapDescription } from './salesforce/metadata';
import { useSalesforceObjectDescriptionQuery } from './salesforce/SalesforceMetadata.generated';
const QUESTION_BRANCH_INTERSECTION_PX = 36;
export function dependsOnQuestion(fields: FormField[], fieldId: string, sourceId: string | null): boolean {
  const visited = new Set<string>();
  let current = fields.find(field => field.id === fieldId);
  while (current?.onlyActiveWhen && !visited.has(current.id)) {
    visited.add(current.id);
    if (current.onlyActiveWhen.fieldId === sourceId) {
      return true;
    }
    current = fields.find(field => field.id === current?.onlyActiveWhen?.fieldId);
  }
  return false;
}
export interface AdminFormBuilderProps {
  initialDraft: FormDraft;
  isPublishing: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSaveDraft: (draft: FormDraft) => Promise<void>;
  onPublish: (draft: FormDraft, hasUnsavedChanges: boolean) => Promise<void>;
  onTestDraft?: (draft: FormDraft) => Promise<void>;
  onTestHistory?: () => void;
  testWorkspace?: ReactNode;
  validationIssues?: GraphQLValidationIssue[];
  onDismissValidationIssues?: () => void;
}
export function hasFormDraftChanges(draft: FormDraftInput, initialDraft: FormDraftInput): boolean {
  return draft.name !== initialDraft.name || draft.description !== initialDraft.description || JSON.stringify(draft.draftConfig) !== JSON.stringify(initialDraft.draftConfig);
}
export function AdminFormBuilder({
  initialDraft,
  isPublishing,
  isSaving,
  onCancel,
  onSaveDraft,
  onPublish,
  onTestDraft,
  onTestHistory,
  testWorkspace,
  validationIssues,
  onDismissValidationIssues
}: AdminFormBuilderProps) {
  const {
    draft,
    setDraft,
    undo,
    redo,
    canUndo,
    canRedo,
    endGroup
  } = useFormDraftHistory(initialDraft);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isExtractionPromptOpen, setIsExtractionPromptOpen] = useState(false);
  const [isPublishConfirmationOpen, setIsPublishConfirmationOpen] = useState(false);
  const [isPublishValidationOpen, setIsPublishValidationOpen] = useState(false);
  const [pendingArrayConversion, setPendingArrayConversion] = useState<string>();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isImportConfirmationOpen, setIsImportConfirmationOpen] = useState(false);
  const [normalize] = useNormalizeFormDraftConfigMutation();
  const sections = draft.draftConfig.form;
  const salesforceMapping = readSalesforceMapping(draft.draftConfig);
  const {
    data: salesforceMetadata,
    error: salesforceMetadataError
  } = useSalesforceObjectDescriptionQuery({
    variables: {
      object: salesforceMapping?.objectAPIName ?? ''
    },
    skip: !salesforceMapping
  });
  const salesforceDescription = useMemo(() => salesforceMetadata ? mapDescription(salesforceMetadata.salesforceObjectDescription) : undefined, [salesforceMetadata]);
  const fields = useMemo(() => getFormFields(sections), [sections]);
  const [selectedFieldId, setSelectedFieldId] = useState(fields[0]?.id ?? null);
  const [selectedSectionId, setSelectedSectionId] = useState(sections[0]?.id ?? null);
  const [sectionPendingRemoval, setSectionPendingRemoval] = useState<FormSection | null>(null);
  const sectionElements = useRef(new Map<string, HTMLDivElement>());
  const [copyToClipboard] = useCopyToClipboard();
  const {
    showFeedback
  } = useFeedback();
  const positions = useMemo(() => getQuestionPositions(sections), [sections]);
  const selectedField = fields.find(field => field.id === selectedFieldId);
  const {
    selectedFieldParent,
    conditionParentFields
  } = useMemo(() => {
    const selectedFieldSection = sections.find(section => getFormFields([section]).some(field_0 => field_0.id === selectedFieldId));
    return {
      selectedFieldParent: fields.find(field_1 => structuralFields(field_1).some(child => child.id === selectedFieldId)),
      conditionParentFields: (selectedFieldSection?.children ?? []).filter(field_2 => field_2.id !== selectedFieldId && !dependsOnQuestion(fields, field_2.id, selectedFieldId) && field_2.fieldType !== 'object' && field_2.fieldType !== 'array' && getOnlyActiveWhenOperators(field_2.fieldType).length > 0)
    };
  }, [sections, fields, selectedFieldId]);
  const validationErrors = (validationIssues ?? []).map(issue => issue.message);
  const isValid = validationErrors.length === 0;
  const hasUnsavedChanges = hasFormDraftChanges(draft, initialDraft);
  const requestCancel = () => {
    if (hasUnsavedChanges) {
      setIsCancelConfirmationOpen(true);
      return;
    }
    onCancel();
  };

  // Read current sections through the state updater so these handlers keep
  // the same identity when they are passed to memoized children.
  const setSections = useCallback((update: (current: FormSection[]) => FormSection[], group?: string) => {
    setDraft(current => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        form: update(current.draftConfig.form)
      }
    }), group);
  }, [setDraft]);
  const updateSection = useCallback((sectionId: string, patch: Partial<Omit<FormSection, 'id'>>) => {
    setSections(current_0 => current_0.map(section_0 => section_0.id === sectionId ? {
      ...section_0,
      ...patch
    } : section_0));
  }, [setSections]);
  const updateField = useCallback((fieldId: string, patch_0: Partial<Omit<FormField, 'id' | 'type'>>, confirmed = false) => {
    const property = Object.keys(patch_0).at(0);
    const group_0 = property === 'label' || property === 'extraction' || property === 'validation' ? `${fieldId}:${property}` : undefined;
    const existing = fields.find(field_3 => field_3.id === fieldId);
    if (patch_0.fieldType && patch_0.fieldType !== existing?.fieldType) {
      if (fields.some(field_4 => field_4.onlyActiveWhen?.fieldId === fieldId)) {
        showFeedback('Remove dependent follow-up conditions before changing this question type.', {
          variant: 'error'
        });
        return;
      }
      if (existing?.fieldType === 'object' && patch_0.fieldType === 'array' && !confirmed) {
        setPendingArrayConversion(fieldId);
        return;
      }
    }
    if ((patch_0.fieldType === 'object' || patch_0.fieldType === 'array') && patch_0.fieldType !== existing?.fieldType && existing?.exports) {
      showFeedback('Remove the destination mapping before changing this question type.', {
        variant: 'error'
      });
      return;
    }
    if ((patch_0.fieldType === 'object' || patch_0.fieldType === 'array') && patch_0.fieldType !== existing?.fieldType && fields.some(field_5 => field_5.dependsOn?.fieldIds?.includes(fieldId))) {
      showFeedback('Remove this question from other questions’ “Uses answers from” before changing its type.', {
        variant: 'error'
      });
      return;
    }
    setDraft(current_1 => {
      const updated = current_1;
      return {
        ...updated,
        draftConfig: {
          ...updated.draftConfig,
          form: updated.draftConfig.form.map(section_1 => ({
            ...section_1,
            children: mapFields(section_1.children, field_6 => {
              if (field_6.id !== fieldId) {
                return field_6;
              }
              let next = field_6;
              if (patch_0.fieldType && patch_0.fieldType !== field_6.fieldType) {
                const {
                  properties: _properties,
                  items: _items,
                  options: _options,
                  validation: _validation,
                  ...common
                } = field_6;
                next = {
                  ...createRecursiveFormNode(patch_0.fieldType, fields.some(parent => structuralFields(parent).some(child_0 => child_0.id === fieldId))),
                  ...common,
                  fieldType: patch_0.fieldType
                };
              }
              next = updateFormField(next, patch_0);
              return next;
            })
          }))
        }
      };
    }, group_0);
  }, [fields, setDraft, showFeedback]);
  const removeField = useCallback((fieldId_0: string) => {
    setSections(current_2 => removeComposerField(current_2, fieldId_0));
    setSelectedFieldId(null);
  }, [setSections]);
  const changeActivationParent = useCallback((fieldId_1: string, parentId: string | null) => {
    setSections(current_3 => setFormFieldActivationParent(current_3, fieldId_1, parentId));
  }, [setSections]);
  const reorderSection = useCallback((oldIndex: number, newIndex: number) => {
    setSections(current_4 => reorderSections(current_4, oldIndex, newIndex));
  }, [setSections]);
  const getSelectedFieldId = useGetState(selectedFieldId);
  const moveField = useCallback((fieldId_2: string, sectionId_0: string, targetFieldId?: string) => {
    setSections(current_5 => moveComposerField(current_5, fieldId_2, sectionId_0, targetFieldId));
    if (fieldId_2 === getSelectedFieldId()) {
      setSelectedSectionId(sectionId_0);
    }
  }, [setSections, getSelectedFieldId]);
  const addSection = () => {
    const section_2 = createBlankSection();
    setSections(current_6 => [...current_6, section_2]);
    setSelectedSectionId(section_2.id);
    setSelectedFieldId(null);
    requestAnimationFrame(() => {
      sectionElements.current.get(section_2.id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  };
  const addField = useCallback((sectionId_1: string) => {
    const field_7 = createBlankField();
    setSections(current_7 => current_7.map(section_3 => section_3.id === sectionId_1 ? {
      ...section_3,
      children: [...section_3.children, field_7]
    } : section_3));
    setSelectedSectionId(sectionId_1);
    setSelectedFieldId(field_7.id);
  }, [setSections]);
  const addSingleSelectFollowUpField = useCallback((parentId_0: string, candidateValue: string) => {
    const field_8 = createBlankField();
    setSections(current_8 => addSingleSelectFollowUp(current_8, parentId_0, candidateValue, field_8));
    setSelectedFieldId(field_8.id);
  }, [setSections]);

  // Clicking a section in the rail is navigation only: scroll the canvas to
  // that section and mark it in the rail. The selected question and the
  // inspector do not change.
  const selectSection = (section_4: FormSection) => {
    sectionElements.current.get(section_4.id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    setSelectedSectionId(section_4.id);
  };
  const getSections = useGetState(sections);
  const selectField = useCallback((fieldId_3: string) => {
    const section_5 = getSections().find(candidate => getFormFields([candidate]).some(field_9 => field_9.id === fieldId_3));
    setSelectedSectionId(section_5?.id ?? null);
    setSelectedFieldId(fieldId_3);
  }, [getSections]);
  const getDraftForPersistence = () => persistComposerDraft(draft);
  const addProperty = (parentId_1: string) => {
    const property_0 = createRecursiveFormNode('shortText', true);
    setSections(current_9 => current_9.map(section_6 => ({
      ...section_6,
      children: mapFields(section_6.children, field_10 => field_10.id === parentId_1 && field_10.fieldType === 'object' ? {
        ...field_10,
        properties: [...(field_10.properties ?? []), property_0]
      } : field_10)
    })));
    setSelectedFieldId(property_0.id);
  };
  const copyDraftConfig = () => {
    copyToClipboard(JSON.stringify(draft.draftConfig, null, 2), () => showFeedback('Draft config JSON copied to clipboard', {
      variant: 'success'
    }), () => showFeedback('Failed to copy draft config JSON', {
      variant: 'error'
    }));
  };
  return <FullScreenModal trowserKey={TrowserKey.Standalone} isOpen onClose={requestCancel}>
      <Stack height="100vh" minWidth={1180} bgcolor={COLORS.PRIMITIVES.WHITE} onBlurCapture={endGroup}>
        <Stack component="header" direction="row" alignItems="center" justifyContent="space-between" px={2} height={60} minHeight={60} borderBottom={`1px solid ${COLORS.GRAY[200]}`}>
          <Box flex={1}>
            <Button size="sm" variant="transparent" startIcon={ChevronLeftIcon} onClick={requestCancel}>
              Exit without saving
            </Button>
          </Box>
          <Stack minWidth={0} flex={1} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <EditableHeader value={draft.name} placeholder="New review" typographyVariant="title-s" editButtonSize="xs" editButtonSpacing={0} testId="form-name" onChange={name => setDraft(current_10 => ({
              ...current_10,
              name
            }))} />
              <Badge variant={BadgeVariants.SecondaryOutline} display={draft.currentPublishedVersion ? `Version ${draft.currentPublishedVersion.version}` : 'Draft'} />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flex={1} justifyContent="flex-end">
            <Stack direction="row">
              <IconButton size="sm" variant="transparent" icon={RefreshCcw01Icon} ariaLabel="Undo" disabled={!canUndo || isSaving || isPublishing} onClick={undo} />
              <IconButton size="sm" variant="transparent" icon={RefreshCw01Icon} ariaLabel="Redo" disabled={!canRedo || isSaving || isPublishing} onClick={redo} />
            </Stack>
            <Button size="sm" variant="secondary" disabled={isSaving || isPublishing} onClick={() => {
            if (!isValid) {
              setIsPublishValidationOpen(true);
              return;
            }
            setIsPublishConfirmationOpen(true);
          }}>
              Publish
            </Button>
            <Button size="sm" variant="primary" disabled={isSaving || isPublishing} onClick={async () => onSaveDraft(getDraftForPersistence())}>
              Save draft
            </Button>
            <ContextMenuButton variant="transparent" aria-label="Form actions">
              {onTestHistory && <MenuItem label="View tests" onClick={onTestHistory} />}
              {onTestDraft && <MenuItem label="New test" disabled={!isValid || isSaving || isPublishing || !!testWorkspace} onClick={async () => onTestDraft(getDraftForPersistence())} />}
              <MenuItem label="Copy JSON" disabled={isSaving || isPublishing} onClick={copyDraftConfig} />
              <MenuItem label="Update from JSON" disabled={isSaving || isPublishing} onClick={() => {
              if (hasUnsavedChanges) {
                setIsImportConfirmationOpen(true);
              } else {
                setIsImportOpen(true);
              }
            }} />
              <MenuItem label="Edit extraction prompt" muiMenuItemProps={{
              'aria-label': 'Edit form extraction prompt'
            }} onClick={() => setIsExtractionPromptOpen(true)} />
            </ContextMenuButton>
          </Stack>
        </Stack>
        {testWorkspace}
        {salesforceMapping && <SalesforceDestinationBanner mapping={salesforceMapping} description={salesforceDescription} />}
        {validationIssues && validationIssues.length > 0 && <Box px={2} py={1}>
            <FormValidationIssues issues={validationIssues} onSelectField={selectField} onDismiss={() => onDismissValidationIssues?.()} />
          </Box>}
        <Box display="flex" flex={1} minHeight={0} overflow="hidden">
          <FormBuilderWorkspace sectionRail={<SectionRail sections={sections} selectedSectionId={selectedSectionId ?? undefined} onAddField={addField} onAddSection={addSection} onRemoveSection={setSectionPendingRemoval} onReorder={reorderSection} onSelectSection={selectSection} />} canvas={<QuestionCanvas onAddProperty={addProperty} onRemoveField={removeField} sections={sections} allFields={fields} positions={positions} selectedFieldId={selectedFieldId} sectionElements={sectionElements.current} onAddField={addField} onMoveField={moveField} onSelectField={selectField} onUpdateSection={updateSection} />} inspector={<QuestionInspector depth={selectedField ? composerSchemaDepth(fields, selectedField.id) : 1} field={selectedField} canAddFollowUp={sections.some(section_7 => section_7.children.some(field_11 => field_11.id === selectedFieldId))} hasSalesforceAnswer={Boolean(selectedField?.exports?.salesforce)} salesforceEditor={salesforceMapping && selectedField ? <SalesforceQuestionMappingEditor key={selectedField.id} field={selectedField} mapping={salesforceMapping} destinations={salesforceDescription?.fields} failed={Boolean(salesforceMetadataError)} onAnswerChange={(destination, sentAs) => setDraft(current_11 => ({
          ...current_11,
          draftConfig: setSalesforceAnswerDestination(current_11.draftConfig, selectedField.id, destination, sentAs)
        }))} onAnswerRemove={fieldAPIName => setDraft(current_12 => ({
          ...current_12,
          draftConfig: removeSalesforceDestination(current_12.draftConfig, fieldAPIName)
        }))} onEvidenceChange={evidence => setDraft(current_13 => ({
          ...current_13,
          draftConfig: setSalesforceEvidenceDestination(current_13.draftConfig, selectedField.id, evidence)
        }))} /> : undefined} position={selectedField ? positions.get(selectedField.id) : undefined} conditionParentFields={conditionParentFields} sections={sections} positions={positions} parentField={selectedFieldParent} onChangeActivationParent={changeActivationParent} onAddSingleSelectFollowUp={addSingleSelectFollowUpField} onChangeField={updateField} onRemoveField={removeField} />} />
        </Box>
      </Stack>
      {isPublishValidationOpen && <DialogModal isOpen heading="Cannot publish review" onClose={() => setIsPublishValidationOpen(false)} dialogPaperSx={{
      width: 640
    }} actions={<Button size="sm" variant="primary" onClick={() => setIsPublishValidationOpen(false)}>
              Back to editor
            </Button>}>
          <Typography variant="body-s">
            Fix these issues, then publish again.
          </Typography>
          <Box component="ul" sx={{
        pl: 3
      }}>
            {validationErrors.map(error => <Typography component="li" variant="body-s" key={error} sx={{
          mb: 1
        }}>
                {error}
              </Typography>)}
          </Box>
        </DialogModal>}
      {pendingArrayConversion && <ConfirmationModal isOpen heading="Change object to array?" confirmText="Change type" onClose={reason => {
      if (reason === 'confirm') {
        updateField(pendingArrayConversion, {
          fieldType: 'array'
        }, true);
      }
      setPendingArrayConversion(undefined);
    }}>
          This change removes the object properties. You can undo this change.
        </ConfirmationModal>}
      {isPublishConfirmationOpen && <ConfirmationModal isOpen heading="Publish form" confirmText="Publish" onClose={reason_0 => {
      setIsPublishConfirmationOpen(false);
      if (reason_0 === 'confirm') {
        void onPublish(getDraftForPersistence(), hasUnsavedChanges);
      }
    }}>
          Make this form available to everyone. Each time you publish a new
          version is created.
        </ConfirmationModal>}
      {isCancelConfirmationOpen && <ConfirmationModal isOpen heading="Discard unsaved changes?" confirmText="Discard changes" confirmButtonProps={{
      variant: 'destructive-primary'
    }} onClose={reason_1 => {
      setIsCancelConfirmationOpen(false);
      if (reason_1 === 'confirm') {
        onCancel();
      }
    }}>
          Your unsaved changes will be lost.
        </ConfirmationModal>}
      {isExtractionPromptOpen && <FormExtractionPromptModal prompt={draft.draftConfig.extractionContext.prompt} onClose={() => setIsExtractionPromptOpen(false)} onApply={prompt => {
      setDraft(current_14 => ({
        ...current_14,
        draftConfig: {
          ...current_14.draftConfig,
          extractionContext: {
            prompt
          }
        }
      }));
      setIsExtractionPromptOpen(false);
    }} />}
      {isImportConfirmationOpen && <ConfirmationModal isOpen heading="Replace unsaved draft config?" confirmText="Continue" onClose={reason_2 => {
      setIsImportConfirmationOpen(false);
      if (reason_2 === 'confirm') {
        setIsImportOpen(true);
      }
    }}>
          Applying JSON will replace your unsaved draft config. The form name
          and description will stay unchanged.
        </ConfirmationModal>}
      {isImportOpen && <FormJsonImportModal mode="update" onClose={() => setIsImportOpen(false)} onApply={async json => {
      const result = await normalize({
        variables: {
          json
        }
      });
      const normalizedConfig: unknown = result.data?.normalizeFormDraftConfig;
      const imported = hydrateFormDraft({
        ...draft,
        draftConfig: normalizedConfig
      });
      if (!imported) {
        throw new Error('Normalized draft is unsupported');
      }
      setDraft(current_15 => ({
        ...current_15,
        draftConfig: imported.draftConfig
      }));
      setSelectedSectionId(imported.draftConfig.form[0]?.id ?? null);
      setSelectedFieldId(getFormFields(imported.draftConfig.form)[0]?.id ?? null);
    }} />}
      {sectionPendingRemoval && <ConfirmationModal isOpen heading="Remove section?" confirmText="Remove section" confirmButtonProps={{
      variant: 'destructive-primary'
    }} onClose={reason_3 => {
      if (reason_3 === 'confirm') {
        const removedFieldIds = new Set(getFormFields([sectionPendingRemoval]).map(field_12 => field_12.id));
        const remainingSections = removeFormSection(sections, sectionPendingRemoval.id);
        setSections(current_16 => removeFormSection(current_16, sectionPendingRemoval.id));
        if (selectedSectionId === sectionPendingRemoval.id) {
          setSelectedSectionId(remainingSections[0]?.id ?? null);
        }
        if (selectedFieldId && removedFieldIds.has(selectedFieldId)) {
          setSelectedFieldId(null);
        }
      }
      setSectionPendingRemoval(null);
    }}>
          This removes the section and all of its questions from the draft.
        </ConfirmationModal>}
    </FullScreenModal>;
}

/** Mount measurement with the modal content, after its portal has attached. */
function FormBuilderWorkspace({
  sectionRail,
  canvas,
  inspector
}: {
  sectionRail: ReactNode;
  canvas: ReactNode;
  inspector: ReactNode;
}) {
  const {
    panelsRef,
    sectionRail: railResize,
    inspector: resize
  } = useFormBuilderPanelWidths();
  return <Box ref={panelsRef} display="flex" flex={1} minWidth={0} overflow="auto">
      <Stack id="form-section-rail" width={railResize.panelWidth} flexShrink={0} minHeight={0}>
        {sectionRail}
      </Stack>
      <ResizeLine ref={railResize.resizeRef} isResizing={railResize.isResizing} onMouseDown={railResize.handleMouseDown} />
      {canvas}
      <ResizeLine ref={resize.resizeRef} isResizing={resize.isResizing} onMouseDown={resize.handleMouseDown} />
      <Box id="form-question-inspector" width={resize.panelWidth} minWidth={360} flexShrink={0} overflow="auto">
        {inspector}
      </Box>
    </Box>;
}
interface SectionRailProps {
  sections: FormSection[];
  selectedSectionId?: string;
  onAddField: (sectionId: string) => void;
  onAddSection: () => void;
  onRemoveSection: (section: FormSection) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onSelectSection: (section: FormSection) => void;
}

// Row height shared by the rail header and every section row. It matches the
// presentation "Pages" tree so the two side panels read the same.
const SECTION_RAIL_ROW_HEIGHT = 40;

/**
 * The left panel that lists sections. It follows the presentation "Pages"
 * panel: an eyebrow header with an add button, then one row per section with
 * a drag handle that shows on hover, the section name, and an overflow menu.
 * Dragging a row is the only way to reorder sections.
 */
function SectionRail(t0) {
  const $ = _c(26);
  const {
    sections,
    selectedSectionId,
    onAddField,
    onAddSection,
    onRemoveSection,
    onReorder,
    onSelectSection
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Typography variant="eyebrow-xs" sx={{
      flexGrow: 1,
      color: COLORS.TEXT.SECONDARY
    }}>Sections</Typography>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onAddSection) {
    t2 = <Stack direction="row" alignItems="center" minHeight={SECTION_RAIL_ROW_HEIGHT} boxSizing="content-box" pt={1} pl={2} pr={0.5}>{t1}<IconButton icon={PlusIcon} size="xs" variant="transparent" ariaLabel="Add section" onClick={onAddSection} /></Stack>;
    $[1] = onAddSection;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onAddField || $[4] !== onRemoveSection || $[5] !== onSelectSection || $[6] !== sections || $[7] !== selectedSectionId) {
    let t4;
    if ($[9] !== onAddField || $[10] !== onRemoveSection || $[11] !== onSelectSection || $[12] !== selectedSectionId) {
      t4 = section_0 => <SortableSectionNavItem key={section_0.id} section={section_0} isSelected={section_0.id === selectedSectionId} onAddField={onAddField} onRemoveSection={onRemoveSection} onSelect={onSelectSection} />;
      $[9] = onAddField;
      $[10] = onRemoveSection;
      $[11] = onSelectSection;
      $[12] = selectedSectionId;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = sections.map(t4);
    $[3] = onAddField;
    $[4] = onRemoveSection;
    $[5] = onSelectSection;
    $[6] = sections;
    $[7] = selectedSectionId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[14] !== onReorder || $[15] !== sections || $[16] !== t3) {
    t4 = <SortableList items={sections} getId={_temp} onReorder={onReorder}>{t3}</SortableList>;
    $[14] = onReorder;
    $[15] = sections;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== sections.length) {
    t5 = sections.length === 0 && <Typography variant="body-xs" px={2} py={1} sx={{
      color: COLORS.TEXT.SECONDARY
    }}>No sections</Typography>;
    $[18] = sections.length;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <Stack flex={1} minHeight={0} overflow="auto">{t4}{t5}</Stack>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t2 || $[24] !== t6) {
    t7 = <Stack flex={1} minHeight={0} bgcolor={COLORS.PRIMITIVES.WHITE} overflow="hidden">{t2}{t6}</Stack>;
    $[23] = t2;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp(section) {
  return section.id;
}
interface SortableSectionNavItemProps {
  section: FormSection;
  isSelected: boolean;
  onAddField: (sectionId: string) => void;
  onRemoveSection: (section: FormSection) => void;
  onSelect: (section: FormSection) => void;
}
function SortableSectionNavItem(t0) {
  const $ = _c(50);
  const {
    section,
    isSelected,
    onAddField,
    onRemoveSection,
    onSelect
  } = t0;
  const [isHovering, setIsHovering] = useState(false);
  let t1;
  if ($[0] !== section.id) {
    t1 = {
      id: section.id
    };
    $[0] = section.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t1);
  const name = section.label || "New section";
  let t2;
  if ($[2] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[2] = transform;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isDragging ? 0.6 : 1;
  let t4;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== transition) {
    t4 = {
      transform: t2,
      transition,
      opacity: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = transition;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isSelected ? COLORS.FUNCTIONAL.HOVER : undefined;
  let t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      "&:hover": {
        bgcolor: COLORS.FUNCTIONAL.HOVER
      }
    };
    t7 = () => setIsHovering(true);
    t8 = () => setIsHovering(false);
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  const t9 = `Reorder section ${name}`;
  const t10 = isHovering || isDragging ? 1 : 0;
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 1
    };
    t12 = {
      opacity: 1
    };
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10) {
    t13 = {
      color: COLORS.NEUTRAL_GRAY[500],
      cursor: "ns-resize",
      opacity: t10,
      touchAction: "none",
      "&:focus-visible": t11,
      "@media (hover: none)": t12
    };
    $[13] = t10;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== attributes || $[16] !== listeners || $[17] !== t13 || $[18] !== t9) {
    t14 = <IconButton icon={DotsMoveIcon} size="xs" variant="transparent" ariaLabel={t9} sx={t13} {...attributes} {...listeners} />;
    $[15] = attributes;
    $[16] = listeners;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== onSelect || $[21] !== section) {
    t15 = () => onSelect(section);
    $[20] = onSelect;
    $[21] = section;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      flex: 1,
      justifyContent: "flex-start",
      minWidth: 0,
      alignSelf: "stretch",
      textAlign: "left"
    };
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      color: COLORS.TEXT.PRIMARY
    };
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== name) {
    t18 = <Typography variant="body-xs" noWrap={true} px={1} sx={t17}>{name}</Typography>;
    $[25] = name;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t15 || $[28] !== t18) {
    t19 = <ButtonBase onClick={t15} sx={t16}>{t18}</ButtonBase>;
    $[27] = t15;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = `Section ${name} actions`;
  let t21;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <PlusIcon size={18} />;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== onAddField || $[32] !== section.id) {
    t22 = <MenuItem icon={t21} label="Add question" onClick={() => onAddField(section.id)} />;
    $[31] = onAddField;
    $[32] = section.id;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Trash01Icon color={COLORS.FUNCTIONAL.ERROR.DEFAULT} size={18} />;
    t24 = <Box component="span" color={COLORS.FUNCTIONAL.ERROR.DEFAULT}>Delete section</Box>;
    $[34] = t23;
    $[35] = t24;
  } else {
    t23 = $[34];
    t24 = $[35];
  }
  let t25;
  if ($[36] !== onRemoveSection || $[37] !== section) {
    t25 = <MenuItem icon={t23} label={t24} onClick={() => onRemoveSection(section)} />;
    $[36] = onRemoveSection;
    $[37] = section;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t20 || $[40] !== t22 || $[41] !== t25) {
    t26 = <ContextMenuButton variant="transparent" size="xs" aria-label={t20}>{t22}{t25}</ContextMenuButton>;
    $[39] = t20;
    $[40] = t22;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== setNodeRef || $[44] !== t14 || $[45] !== t19 || $[46] !== t26 || $[47] !== t4 || $[48] !== t5) {
    t27 = <Stack ref={setNodeRef} style={t4} direction="row" alignItems="center" height={SECTION_RAIL_ROW_HEIGHT} pl={1} pr={0.5} bgcolor={t5} sx={t6} onMouseEnter={t7} onMouseLeave={t8}>{t14}{t19}{t26}</Stack>;
    $[43] = setNodeRef;
    $[44] = t14;
    $[45] = t19;
    $[46] = t26;
    $[47] = t4;
    $[48] = t5;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  return t27;
}
const dragOverlayCardSx = {
  boxShadow: 3,
  cursor: 'ns-resize'
} as const;
interface QuestionCanvasProps {
  onAddProperty: (parentId: string) => void;
  onRemoveField: (fieldId: string) => void;
  sections: FormSection[];
  allFields: FormField[];
  positions: Map<string, string>;
  selectedFieldId: string | null;
  sectionElements: Map<string, HTMLDivElement>;
  onAddField: (sectionId: string) => void;
  onMoveField: (fieldId: string, sectionId: string, targetFieldId?: string) => void;
  onSelectField: (fieldId: string) => void;
  onUpdateSection: (sectionId: string, patch: Partial<Omit<FormSection, 'id'>>) => void;
}
function QuestionCanvas(t0) {
  const $ = _c(46);
  const {
    onAddProperty,
    onRemoveField,
    sections,
    allFields,
    positions,
    selectedFieldId,
    sectionElements,
    onAddField,
    onMoveField,
    onSelectField,
    onUpdateSection
  } = t0;
  const sensors = useDndKitSensors();
  let t1;
  if ($[0] !== sections) {
    t1 = new Map(sections.map(_temp2));
    $[0] = sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rowsBySectionId = t1;
  let t2;
  if ($[2] !== rowsBySectionId) {
    t2 = args => {
      const allowedDropIds = getAllowedDropIds(rowsBySectionId, String(args.active.id));
      return detectQuestionCollisions(args, allowedDropIds);
    };
    $[2] = rowsBySectionId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const collisionDetection = t2;
  let t3;
  if ($[4] !== onMoveField) {
    t3 = t4 => {
      const {
        active,
        over
      } = t4;
      if (!over) {
        return;
      }
      const targetSectionId = over.data.current?.sectionId;
      if (typeof targetSectionId !== "string") {
        return;
      }
      const targetFieldId = over.data.current?.fieldId;
      onMoveField(String(active.id), targetSectionId, typeof targetFieldId === "string" ? targetFieldId : undefined);
    };
    $[4] = onMoveField;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleDragEnd = t3;
  let t4;
  if ($[6] !== sections.length) {
    t4 = sections.length === 0 && <Stack alignItems="center" justifyContent="center" minHeight={240}><Typography variant="body-s">Add a section to start authoring questions.</Typography></Stack>;
    $[6] = sections.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== allFields || $[9] !== onAddField || $[10] !== onAddProperty || $[11] !== onMoveField || $[12] !== onRemoveField || $[13] !== onSelectField || $[14] !== onUpdateSection || $[15] !== positions || $[16] !== rowsBySectionId || $[17] !== sectionElements || $[18] !== sections || $[19] !== selectedFieldId) {
    let t6;
    if ($[21] !== allFields || $[22] !== onAddField || $[23] !== onAddProperty || $[24] !== onMoveField || $[25] !== onRemoveField || $[26] !== onSelectField || $[27] !== onUpdateSection || $[28] !== positions || $[29] !== rowsBySectionId || $[30] !== sectionElements || $[31] !== selectedFieldId) {
      t6 = (section_0, sectionIndex) => <QuestionSection onAddProperty={onAddProperty} onRemoveField={onRemoveField} onMoveField={onMoveField} key={section_0.id} section={section_0} allFields={allFields} positions={positions} rows={rowsBySectionId.get(section_0.id) ?? []} sectionIndex={sectionIndex} selectedFieldId={selectedFieldId} sectionElements={sectionElements} onAddField={onAddField} onSelectField={onSelectField} onUpdateSection={onUpdateSection} />;
      $[21] = allFields;
      $[22] = onAddField;
      $[23] = onAddProperty;
      $[24] = onMoveField;
      $[25] = onRemoveField;
      $[26] = onSelectField;
      $[27] = onUpdateSection;
      $[28] = positions;
      $[29] = rowsBySectionId;
      $[30] = sectionElements;
      $[31] = selectedFieldId;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    t5 = sections.map(t6);
    $[8] = allFields;
    $[9] = onAddField;
    $[10] = onAddProperty;
    $[11] = onMoveField;
    $[12] = onRemoveField;
    $[13] = onSelectField;
    $[14] = onUpdateSection;
    $[15] = positions;
    $[16] = rowsBySectionId;
    $[17] = sectionElements;
    $[18] = sections;
    $[19] = selectedFieldId;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[33] !== allFields || $[34] !== positions || $[35] !== selectedFieldId) {
    t6 = <QuestionDragOverlay allFields={allFields} positions={positions} selectedFieldId={selectedFieldId} />;
    $[33] = allFields;
    $[34] = positions;
    $[35] = selectedFieldId;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== collisionDetection || $[38] !== handleDragEnd || $[39] !== sensors || $[40] !== t5 || $[41] !== t6) {
    t7 = <DndContext sensors={sensors} collisionDetection={collisionDetection} measuring={measuringConfig} onDragEnd={handleDragEnd}>{t5}{t6}</DndContext>;
    $[37] = collisionDetection;
    $[38] = handleDragEnd;
    $[39] = sensors;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== t4 || $[44] !== t7) {
    t8 = <Stack bgcolor={COLORS.PRIMITIVES.WHITE} borderRight={`1px solid ${COLORS.GRAY[200]}`} minWidth={480} flex={1} overflow="auto" p={2} spacing={4}>{t4}{t7}</Stack>;
    $[43] = t4;
    $[44] = t7;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  return t8;
}
function _temp2(section) {
  return [section.id, getNestedQuestionRows(section)];
}
export function detectQuestionCollisions(args: Parameters<CollisionDetection>[0], allowedDropIds: Set<string>): ReturnType<CollisionDetection> {
  const filteredArgs = {
    ...args,
    droppableContainers: args.droppableContainers.filter(container => allowedDropIds.has(String(container.id)))
  };
  return args.pointerCoordinates ? pointerWithin(filteredArgs) : rectIntersection(filteredArgs);
}

/**
 * Return the cards that the dragged card may be dropped on. An attached
 * conditional question can only replace a sibling under the same parent. A
 * top-level question can replace any top-level question or enter a section.
 */
function getAllowedDropIds(rowsBySectionId: Map<string, NestedQuestionRow[]>, activeFieldId: string): Set<string> {
  const allowed = new Set<string>();
  rowsBySectionId.forEach(rows => {
    const activeRow = rows.find(row => row.field.id === activeFieldId);
    if (activeRow?.parentId) {
      rows.filter(row => row.parentId === activeRow.parentId && row.structural === activeRow.structural).forEach(row => allowed.add(row.field.id));
    }
  });
  if (allowed.size > 0) {
    return allowed;
  }
  rowsBySectionId.forEach((rows, sectionId) => {
    allowed.add(`section-drop-${sectionId}`);
    rows.filter(row => row.parentId === null).forEach(row => allowed.add(row.field.id));
  });
  allowed.delete(activeFieldId);
  return allowed;
}
interface QuestionDragOverlayProps {
  allFields: FormField[];
  positions: Map<string, string>;
  selectedFieldId: string | null;
}

/**
 * Render the card that follows the pointer across sections. Read the active
 * card from dnd-kit so only this small component renders when a drag starts.
 */
function QuestionDragOverlay(t0) {
  const $ = _c(9);
  const {
    allFields,
    positions,
    selectedFieldId
  } = t0;
  const {
    active
  } = useDndContext();
  let T0;
  let t1;
  if ($[0] !== active || $[1] !== allFields || $[2] !== positions || $[3] !== selectedFieldId) {
    const activeField = active ? allFields.find(field => field.id === active.id) : undefined;
    T0 = DragOverlay;
    t1 = activeField && <QuestionCardContent field={activeField} isSelected={activeField.id === selectedFieldId} position={positions.get(activeField.id) ?? ""} conditionSummary={getConditionSummary(activeField, allFields)} sx={dragOverlayCardSx} />;
    $[0] = active;
    $[1] = allFields;
    $[2] = positions;
    $[3] = selectedFieldId;
    $[4] = T0;
    $[5] = t1;
  } else {
    T0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== T0 || $[7] !== t1) {
    t2 = <T0>{t1}</T0>;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
interface QuestionSectionProps {
  onAddProperty: (parentId: string) => void;
  onRemoveField: (fieldId: string) => void;
  onMoveField: QuestionCanvasProps['onMoveField'];
  section: FormSection;
  allFields: FormField[];
  positions: Map<string, string>;
  rows: NestedQuestionRow[];
  sectionIndex: number;
  selectedFieldId: string | null;
  sectionElements: Map<string, HTMLDivElement>;
  onAddField: (sectionId: string) => void;
  onSelectField: (fieldId: string) => void;
  onUpdateSection: (sectionId: string, patch: Partial<Omit<FormSection, 'id'>>) => void;
}
function QuestionSection({
  onAddProperty,
  onRemoveField,
  onMoveField,
  section,
  allFields,
  positions,
  rows,
  sectionIndex,
  selectedFieldId,
  sectionElements,
  onAddField,
  onSelectField,
  onUpdateSection
}: QuestionSectionProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const visibleRows = useMemo(() => {
    let hiddenDepth: number | undefined;
    return rows.filter(row => {
      if (hiddenDepth !== undefined && row.depth > hiddenDepth) {
        return false;
      }
      hiddenDepth = collapsed.has(row.field.id) ? row.depth : undefined;
      return true;
    });
  }, [rows, collapsed]);
  const {
    active,
    over,
    setNodeRef
  } = useDroppable({
    id: `section-drop-${section.id}`,
    data: {
      sectionId: section.id
    }
  });
  const isOverSection = Boolean(active) && over?.data.current?.sectionId === section.id;
  const crossSectionTargetFieldId = active && active.data.current?.sectionId !== section.id && isOverSection && typeof over?.data.current?.fieldId === 'string' ? over.data.current.fieldId : null;
  const activeRowHeight = active?.rect.current.initial?.height ?? 0;
  // `useSortable` compares this array by reference to decide whether the list
  // changed. A fresh array on every render makes it treat each drag-over
  // render as a list change and disable the shift transition, so neighbours
  // teleport instead of sliding. Keep the same array while `rows` is stable.
  const rowIds = useMemo(() => visibleRows.map(({
    field
  }) => field.id), [visibleRows]);
  return <Stack ref={(element: HTMLDivElement | null) => {
    setNodeRef(element);
    if (element) {
      sectionElements.set(section.id, element);
    } else {
      sectionElements.delete(section.id);
    }
  }} spacing={1.5} borderRadius={1} minHeight={section.children.length === 0 ? 120 : undefined}
  // Sections have no card of their own; the questions sit directly on
  // the white canvas. Question cards and their section are nested drop
  // targets, so use their shared section ID to keep the section outlined
  // while a card remains the precise reorder target.
  sx={{
    outline: isOverSection ? `1px dashed ${COLORS.BLUE[500]}` : undefined,
    outlineOffset: 8
  }}>
      <QuestionSectionHeader section={section} sectionIndex={sectionIndex} onUpdateSection={onUpdateSection} />
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        <Stack spacing={1}>{renderRows(null)}</Stack>
      </SortableContext>
      {section.children.length === 0 && <Typography variant="body-xs" sx={{
      color: COLORS.TEXT.SECONDARY
    }}>
          Add a question to this section.
        </Typography>}
      <Button size="sm" variant="secondary" startIcon={PlusIcon} fullWidth onClick={() => onAddField(section.id)}>
        Add question
      </Button>
    </Stack>;
  function renderRows(parentId: string | null): ReactNode {
    // Nested rails need the control slot. At the root, reserve it only when
    // the section has hierarchy, so mixed siblings stay aligned when collapsed.
    const showControlSlot = Boolean(parentId) || rows.some(row_0 => row_0.parentId !== null || row_0.field.fieldType === 'object' || row_0.field.fieldType === 'array');
    return visibleRows.filter(row_1 => row_1.parentId === parentId).map(({
      field: field_0
    }, index, siblings) => {
      const container = childFields(field_0).length > 0 || field_0.fieldType === 'object' || field_0.fieldType === 'array';
      const expanded = container && !collapsed.has(field_0.id);
      return <QuestionBranch key={field_0.id} nested={Boolean(parentId)} container={container} lastSibling={index === siblings.length - 1}>
            <Collapse in={crossSectionTargetFieldId === field_0.id}>
              <Box height={activeRowHeight} mb={1} />
            </Collapse>
            <QuestionRow expanded={expanded}>
              {container && <IconButton ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${field_0.label || 'question'}`} aria-expanded={expanded} aria-controls={`question-children-${field_0.id}`} icon={expanded ? MinusCircleIcon : PlusCircleIcon} size="xs" variant="transparent" sx={{
            width: 24,
            height: 24,
            flexShrink: 0,
            bgcolor: COLORS.PRIMITIVES.WHITE,
            zIndex: 1
          }} onClick={() => {
            if (expanded && getFormFields([{
              ...section,
              children: childFields(field_0)
            }]).some(child => child.id === selectedFieldId)) {
              onSelectField(field_0.id);
            }
            setCollapsed(current => {
              const next = new Set(current);
              if (expanded) {
                next.add(field_0.id);
              } else {
                next.delete(field_0.id);
              }
              return next;
            });
          }} />}
              {!container && showControlSlot && <Box width={24} flexShrink={0} />}
              <Box flex={1} minWidth={0}>
                <SortableQuestionCard actions={<ContextMenuButton size="xs" variant="transparent" aria-label={`Question ${field_0.label || positions.get(field_0.id)} actions`}>
                      {['up', 'down'].map(direction => {
                const source = rows.find(row_2 => row_2.field.id === field_0.id);
                const siblings_0 = rows.filter(row_3 => row_3.parentId === source?.parentId && row_3.structural === source?.structural);
                const index_0 = siblings_0.findIndex(row_4 => row_4.field.id === field_0.id);
                const target = siblings_0[index_0 + (direction === 'up' ? -1 : 1)];
                return <MenuItem key={direction} label={`Move ${direction}`} disabled={!target} onClick={() => target && onMoveField(field_0.id, section.id, target.field.id)} />;
              })}
                      <MenuItem label="Remove question" icon={<Trash01Icon size={16} />} disabled={rows.some(row_5 => structuralFields(row_5.field).some(child_0 => child_0.id === field_0.id) && (row_5.field.fieldType === 'array' || row_5.field.properties?.length === 1))} onClick={() => onRemoveField(field_0.id)} />
                    </ContextMenuButton>} itemDefinition={rows.some(row_6 => row_6.field.fieldType === 'array' && row_6.field.items?.id === field_0.id)} field={field_0} allFields={allFields} sectionId={section.id} isSelected={field_0.id === selectedFieldId} position={positions.get(field_0.id) ?? ''} onSelectField={onSelectField} />
              </Box>
            </QuestionRow>
            {container && <Stack id={`question-children-${field_0.id}`} hidden={!expanded} sx={{
          display: expanded ? 'flex' : 'none',
          ml: 1.5,
          pl: 1.5,
          pt: 1,
          gap: 1,
          borderLeft: '1px solid transparent'
        }}>
                {expanded && renderRows(field_0.id)}
                {expanded && field_0.fieldType === 'object' && composerSchemaDepth(allFields, field_0.id) < RECURSIVE_FORM_LIMITS.depth && <Button size="xs" variant="transparent" startIcon={PlusIcon} sx={{
            alignSelf: 'flex-start',
            ml: 3.5
          }} onClick={() => onAddProperty(field_0.id)}>
                      Add property
                    </Button>}
              </Stack>}
          </QuestionBranch>;
    });
  }
}
interface QuestionBranchProps {
  nested: boolean;
  container: boolean;
  lastSibling: boolean;
  children: ReactNode;
}
function QuestionBranch(t0) {
  const $ = _c(7);
  const {
    nested,
    container,
    lastSibling,
    children
  } = t0;
  let t1;
  if ($[0] !== container || $[1] !== lastSibling || $[2] !== nested) {
    t1 = nested ? {
      position: "relative",
      "&::before": {
        content: "\"\"",
        position: "absolute",
        left: -13,
        top: QUESTION_BRANCH_INTERSECTION_PX,
        width: container ? 25 : 41,
        borderTop: `1px solid ${COLORS.GRAY[300]}`,
        pointerEvents: "none"
      },
      "&::after": {
        content: "\"\"",
        position: "absolute",
        left: -13,
        top: -8,
        bottom: lastSibling ? `calc(100% - ${QUESTION_BRANCH_INTERSECTION_PX}px)` : 0,
        borderLeft: `1px solid ${COLORS.GRAY[300]}`,
        pointerEvents: "none"
      }
    } : undefined;
    $[0] = container;
    $[1] = lastSibling;
    $[2] = nested;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== t1) {
    t2 = <Box sx={t1}>{children}</Box>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function QuestionRow(t0) {
  const $ = _c(5);
  const {
    expanded,
    children
  } = t0;
  let t1;
  if ($[0] !== expanded) {
    t1 = expanded ? {
      "&::before": {
        content: "\"\"",
        position: "absolute",
        left: 12,
        top: "50%",
        bottom: 0,
        borderLeft: `1px solid ${COLORS.GRAY[300]}`,
        pointerEvents: "none"
      }
    } : undefined;
    $[0] = expanded;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Stack direction="row" alignItems="center" gap={0.5} position="relative" sx={t1}>{children}</Stack>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type QuestionSectionHeaderProps = Pick<QuestionSectionProps, 'section' | 'sectionIndex' | 'onUpdateSection'>;

/**
 * Render the editable section name. A section is a drop target and renders for
 * drag updates, but this header does not depend on drag state.
 */
const QuestionSectionHeader = memo(function QuestionSectionHeader(t0) {
  const $ = _c(8);
  const {
    section,
    sectionIndex,
    onUpdateSection
  } = t0;
  const sectionNumber = sectionIndex + 1;
  const t1 = `section-name-${section.id}`;
  const t2 = `Section ${sectionNumber} name`;
  let t3;
  if ($[0] !== onUpdateSection || $[1] !== section.id) {
    t3 = label => onUpdateSection(section.id, {
      label
    });
    $[0] = onUpdateSection;
    $[1] = section.id;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== section.label || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <InlineTitleEditor id={t1} ariaLabel={t2} value={section.label} placeholder="New section" onChange={t3} />;
    $[3] = section.label;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
});

// dnd-kit only animates the dragged item into its final slot. A dropped
// question can land somewhere other than its preview slot (attached questions
// snap next to their parent, cross-section drops collapse the source section),
// so the other rows must animate to their new positions too instead of
// snapping there.
const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({
  ...args,
  wasDragging: true
});
interface SortableQuestionCardProps {
  actions?: ReactNode;
  itemDefinition?: boolean;
  field: FormField;
  allFields: FormField[];
  sectionId: string;
  isSelected: boolean;
  position: string;
  onSelectField: (fieldId: string) => void;
}
function SortableQuestionCard(t0) {
  const $ = _c(48);
  const {
    actions,
    itemDefinition,
    field,
    allFields,
    sectionId,
    isSelected,
    position,
    onSelectField
  } = t0;
  let t1;
  if ($[0] !== field.id || $[1] !== sectionId) {
    t1 = {
      fieldId: field.id,
      sectionId
    };
    $[0] = field.id;
    $[1] = sectionId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== field.id || $[4] !== t1) {
    t2 = {
      id: field.id,
      data: t1,
      animateLayoutChanges
    };
    $[3] = field.id;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t2);
  let t3;
  if ($[6] !== allFields || $[7] !== field) {
    t3 = getConditionSummary(field, allFields);
    $[6] = allFields;
    $[7] = field;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const conditionSummary = t3;
  let t4;
  if ($[9] !== field) {
    t4 = getFieldOptionsError(field);
    $[9] = field;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== field) {
    t5 = getMaxLengthError(field);
    $[11] = field;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== field.label) {
    t6 = !field.label.trim() && "Enter a question label.";
    $[13] = field.label;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== field.extraction?.prompt) {
    t7 = !field.extraction?.prompt.trim() && "Enter an extraction prompt.";
    $[15] = field.extraction?.prompt;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = [t4, t5, t6, t7].filter(Boolean);
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const validationMessage = t8.join(" ");
  let t9;
  if ($[22] !== attributes || $[23] !== listeners) {
    t9 = {
      ...attributes,
      ...listeners
    };
    $[22] = attributes;
    $[23] = listeners;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleProps = t9;
  let t10;
  if ($[25] !== field.id || $[26] !== onSelectField) {
    t10 = () => onSelectField(field.id);
    $[25] = field.id;
    $[26] = onSelectField;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleSelect = t10;
  let t11;
  if ($[28] !== transform) {
    t11 = CSS.Translate.toString(transform);
    $[28] = transform;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = isDragging ? 0 : 1;
  let t13;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== transition) {
    t13 = {
      transform: t11,
      transition,
      opacity: t12
    };
    $[30] = t11;
    $[31] = t12;
    $[32] = transition;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== actions || $[35] !== conditionSummary || $[36] !== field || $[37] !== handleProps || $[38] !== handleSelect || $[39] !== isSelected || $[40] !== itemDefinition || $[41] !== position || $[42] !== validationMessage) {
    t14 = <QuestionCardContent actions={actions} itemDefinition={itemDefinition} field={field} validationMessage={validationMessage} isSelected={isSelected} position={position} conditionSummary={conditionSummary} handleProps={handleProps} onSelect={handleSelect} />;
    $[34] = actions;
    $[35] = conditionSummary;
    $[36] = field;
    $[37] = handleProps;
    $[38] = handleSelect;
    $[39] = isSelected;
    $[40] = itemDefinition;
    $[41] = position;
    $[42] = validationMessage;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== setNodeRef || $[45] !== t13 || $[46] !== t14) {
    t15 = <Stack ref={setNodeRef} spacing={0.5} style={t13}>{t14}</Stack>;
    $[44] = setNodeRef;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  return t15;
}
interface QuestionCardContentProps {
  actions?: ReactNode;
  itemDefinition?: boolean;
  field: FormField;
  validationMessage?: string;
  isSelected: boolean;
  position: string;
  conditionSummary?: string;
  handleProps?: React.ComponentProps<typeof ButtonBase>;
  style?: React.CSSProperties;
  sx?: React.ComponentProps<typeof Stack>['sx'];
  onSelect?: () => void;
}

/**
 * The MUI card tree is the expensive part of a sortable row. Render it only
 * when the question, its selection, or its position label changes.
 */
const QuestionCardContent = memo(forwardRef<HTMLDivElement, QuestionCardContentProps>(function QuestionCardContent(t0, ref) {
  const $ = _c(60);
  const {
    actions,
    itemDefinition,
    field,
    validationMessage,
    isSelected,
    position,
    conditionSummary,
    handleProps,
    style,
    sx,
    onSelect
  } = t0;
  const t1 = `1px solid ${isSelected ? COLORS.GRAY[300] : COLORS.GRAY[200]}`;
  const t2 = isSelected ? COLORS.GRAY[50] : COLORS.PRIMITIVES.WHITE;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      "& .question-row-actions": {
        opacity: 0
      },
      "&:hover .question-row-actions, &:focus-within .question-row-actions": {
        opacity: 1
      }
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== sx) {
    t4 = {
      ...sx,
      "@media (hover: hover)": t3
    };
    $[1] = sx;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = `Reorder question ${field.label || "Your first question"}`;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      cursor: "ns-resize",
      p: 1.5,
      touchAction: "none"
    };
    t7 = <DotsMoveIcon size={16} color={COLORS.TEXT.SECONDARY} />;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  let t8;
  if ($[5] !== handleProps || $[6] !== t5) {
    t8 = <ButtonBase aria-label={t5} {...handleProps} sx={t6}>{t7}</ButtonBase>;
    $[5] = handleProps;
    $[6] = t5;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      flex: 1,
      justifyContent: "flex-start",
      minWidth: 0,
      py: 1.5,
      pr: 1.5,
      textAlign: "left"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = !!validationMessage;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      flexShrink: 0,
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none !important"
      }
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const t12 = !!validationMessage;
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none !important"
      }
    };
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      display: "block"
    };
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  const t15 = `Question ${position} needs attention: ${validationMessage}`;
  let t16;
  if ($[12] !== t15) {
    t16 = <AlertTriangleIcon size={16} sx={t14} color={COLORS.FUNCTIONAL.WARNING.DEFAULT} role="img" aria-label={t15} />;
    $[12] = t15;
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  let t17;
  if ($[14] !== t16 || $[15] !== validationMessage) {
    t17 = <Box display="flex" alignItems="center" pr={0.5} sx={t13}><Tooltip title={validationMessage}>{t16}</Tooltip></Box>;
    $[14] = t16;
    $[15] = validationMessage;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== t12 || $[18] !== t17) {
    t18 = <Fade in={t12} timeout={500}>{t17}</Fade>;
    $[17] = t12;
    $[18] = t17;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== t10 || $[21] !== t18) {
    t19 = <Collapse in={t10} orientation="horizontal" timeout={500} sx={t11}>{t18}</Collapse>;
    $[20] = t10;
    $[21] = t18;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  const t20 = field.label || "Your first question";
  let t21;
  if ($[23] !== t20) {
    t21 = <Typography variant="title-xs" noWrap={true}>{t20}</Typography>;
    $[23] = t20;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== field.onlyActiveWhen) {
    t22 = field.onlyActiveWhen && <Badge variant={BadgeVariants.SecondaryOutline} display="Conditional" />;
    $[25] = field.onlyActiveWhen;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t21 || $[28] !== t22) {
    t23 = <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0} mr={0.5}>{t21}{t22}</Stack>;
    $[27] = t21;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== position) {
    t24 = <Typography variant="eyebrow-xs">{position}</Typography>;
    $[30] = position;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] !== t19 || $[33] !== t23 || $[34] !== t24) {
    t25 = <Stack direction="row" alignItems="center" justifyContent="space-between">{t19}{t23}{t24}</Stack>;
    $[32] = t19;
    $[33] = t23;
    $[34] = t24;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      color: COLORS.TEXT.SECONDARY
    };
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  const t27 = itemDefinition ? "Item definition - " : "";
  let t28;
  if ($[37] !== field.fieldType) {
    t28 = getFieldTypeSummary(field.fieldType);
    $[37] = field.fieldType;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  const t29 = conditionSummary ? `, ${conditionSummary}` : "";
  let t30;
  if ($[39] !== t27 || $[40] !== t28 || $[41] !== t29) {
    t30 = <Stack direction="row" alignItems="center" spacing={1}><Typography variant="body-xs" sx={t26}>{t27}{t28}{t29}</Typography></Stack>;
    $[39] = t27;
    $[40] = t28;
    $[41] = t29;
    $[42] = t30;
  } else {
    t30 = $[42];
  }
  let t31;
  if ($[43] !== t25 || $[44] !== t30) {
    t31 = <Stack minWidth={0} width="100%" spacing={0.5}>{t25}{t30}</Stack>;
    $[43] = t25;
    $[44] = t30;
    $[45] = t31;
  } else {
    t31 = $[45];
  }
  let t32;
  if ($[46] !== onSelect || $[47] !== t31) {
    t32 = <ButtonBase onClick={onSelect} sx={t9}>{t31}</ButtonBase>;
    $[46] = onSelect;
    $[47] = t31;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== actions) {
    t33 = <Box className="question-row-actions" pr={0.5}>{actions}</Box>;
    $[49] = actions;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== ref || $[52] !== style || $[53] !== t1 || $[54] !== t2 || $[55] !== t32 || $[56] !== t33 || $[57] !== t4 || $[58] !== t8) {
    t34 = <Stack ref={ref} style={style} direction="row" alignItems="center" border={t1} borderRadius={1} bgcolor={t2} sx={t4}>{t8}{t32}{t33}</Stack>;
    $[51] = ref;
    $[52] = style;
    $[53] = t1;
    $[54] = t2;
    $[55] = t32;
    $[56] = t33;
    $[57] = t4;
    $[58] = t8;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  return t34;
}));
function getConditionSummary(field: FormField, allFields: FormField[]): string | undefined {
  const condition = field.onlyActiveWhen;
  if (!condition) {
    return undefined;
  }
  const source = allFields.find(candidate => candidate.id === condition.fieldId);
  if (!source || !condition.operator) {
    return 'Conditional rule is incomplete';
  }
  const operator = ACTIVATION_OPERATOR_LABELS[condition.operator];
  const candidate = typeof condition.candidateValue === 'string' ? (source.options ?? []).find(option => option.value === condition.candidateValue)?.label || condition.candidateValue : undefined;
  const value = candidate ? ` "${candidate}"` : '';
  return `only shown if "${source.label || 'another question'}" ${operator}${value}`;
}
function getFieldTypeSummary(type: FormField['fieldType']): string {
  if (type === 'object') {
    return 'Object';
  }
  if (type === 'array') {
    return 'Array';
  }
  return FIELD_TYPE_OPTIONS.find(option => option.value === type)?.display ?? type;
}